import defaultAvatar from "@/assets/default_avatar.svg";
import Button from "@/common/elements/Button";
import Select from "@/common/elements/InputElements/Select";
import TextField from "@/common/elements/InputElements/TextField";
import { StyledGridCol, StyledGridRow } from "@/common/styles";
import { useRef, useState, useMemo, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Col, Row } from "react-bootstrap";
import FormItem from "@/common/elements/InputElements/FormItem.jsx";
import Modal from "@/common/elements/Modal";
import axios from "axios";
import API_ENDPOINTS from "@/util/api";
import { Form } from "antd";
import api from "@/services/axios_custom";
import Table from "@/common/elements/Table";
import { setBookings } from "@/slices/bookingsSlice.js";
import {
  getSQLTypeToHTMLType,
  getOptionsFromRefList,
  findAgefromDOB,
  generateRules,
  isNonEmptyVariable,
} from "@/util/common_function";
import {
  BookingFlagsContainer,
  BookingsSectionTitle,
  InmateDetails,
  InmateHeaderContainer,
  InmateInformation,
  InmateName,
  InmatePreviewContainer,
} from "@/components/InmateHeader/styles.js";
import { Image } from "antd";
import useDialog from "@/hooks/useDialog.jsx";

const FILTER_KEY_MAP = {
  lastName: "lName",
  firstName: "fName",
  middleName: "mName",
  suffix: "suffix",
};

const TABLE_HEADERS = [
  { key: "bookingNumber", text: "Booking No" },
  { key: "lastName", text: "Last Name" },
  { key: "firstName", text: "First Name" },
  { key: "middleName", text: "Middle Name" },
  { key: "suffix", text: "Suffix" },
  { key: "dob", text: "DOB" },
  { key: "housingAgency", text: "Housing Agency" },
  { key: "currentLocation", text: "Current Location" },
  { key: "housingLocation", text: "Housing Location" },
];

const SELECTED_INMATE_HEADERS = [
  { key: "bookingNumber", text: "Booking No" },
  { key: "inmateLastName", text: "Last Name" },
  { key: "inmateFirstName", text: "First Name" },
  { key: "inmateMiddleName", text: "Middle Name" },
  { key: "inmateSuffix", text: "Suffix" },
  { key: "inmatePin", text: "Pin" },
];


const InmateToVisit = () => {
  const dispatch = useDispatch();
  const { showDialog } = useDialog();
  const { bookings, bookingsFieldsEnabled } = useSelector((s) => s.bookings);
  const [detailsLoaded, setDetailsLoaded] = useState(false);
  const [selectedInmate, setSelectedInmate] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [detailsRow, setDetailsRow] = useState(null);
  const detailsCache = useRef({});
  const [searchByBookingNo, setSearchByBookingNo] = useState(false);
  const [bookingNumberSearch, setBookingNumberSearch] = useState("");

  const [searchParams, setSearchParams] = useState({
    lastName: "",
    middleName: "",
    firstName: "",
    suffix: "",
  });

  const relationshipOptions = useMemo(
    () => getOptionsFromRefList("Relationship"),
    []
  );
  const [form] = Form.useForm();
  const formRef = useRef(null);
  const bookingDetailsCache = useRef({});
  const bookingDetailsInFlight = useRef({});
  const suffixOptions = useMemo(() => {
    return getOptionsFromRefList("suffix");
  }, []);

  const selectedInmateRows =
    bookings?.visitorLogInmate?.map((i) => ({
      bookingNumber: i.bookingNumber,
      jacketId: i.jacketId,
      inmateLastName: i.inmateLastName,
      inmateFirstName: i.inmateFirstName,
      inmateMiddleName: i.inmateMiddleName,
      inmateSuffix: i.inmateSuffix,
      inmatePin: i.inmatePin,
      visitorLogInmateId: i.visitorLogInmateId
    })) || [];

  const [tableRows, setTableRows] = useState([]);
  const [selectedRow, setSelectedRow] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [tableKey, setTableKey] = useState(0);

  const NO_PAGINATION = {
    pageIndex: 0,
    pageSize: 1000,
  };

  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });

  const cancelSourceRef = useRef(null);

  const previewBio = selectedRow?.rawData?.jackets?.bio?.[0] || null;

  const bioPhotoUrl = previewBio?.photoUrl;
  const defaultInfo = !!previewBio;

  const classLevel = previewBio?.classLevel;
  const inmateType = previewBio?.inmateType;
  const controllingCharge = previewBio?.controllingCharge;
  const assignedLocation = previewBio?.assignedLocation;
  const currentLocation = previewBio?.currentLocation;

  const getRelationshipIdFromLabel = (label) => {
    return relationshipOptions.find(o => o.label === label)?.value ?? null;
  };

  const getRelationshipLabelFromId = (id) => {
    return relationshipOptions.find(o => o.value === id)?.label ?? null;
  };

  const onChangeSearchParams = (eOrValue, explicitName) => {
    if (eOrValue?.target) {
      const { name, value } = eOrValue.target;
      setSearchParams((prev) => ({ ...prev, [name]: value }));
      return;
    }

    if (!explicitName) return;
    setSearchParams((prev) => ({
      ...prev,
      [explicitName]: eOrValue,
    }));
  };

  const formatRowForTable = (row) => {
    const bio = Array.isArray(row.bio) ? row.bio[0] : row.bio || {};
    const arrest = Array.isArray(row.arrestReports) ? row.arrestReports[0] : row.arrestReports || {};

    return {
      bookingNumber: row.bookingNumber || "-",
      lastName: row.lastname || row.lastName || bio.lName || "-",
      firstName: row.firstName || bio.fName || "-",
      middleName: row.middleName || bio.mName || "-",
      suffix: row.suffix || bio.suffix || "-",
      dob: row.dob || bio.dob || "-",
      housingAgency: row.housingAgency || arrest.housingAgency || "-",
      currentLocation: row.currentLocation || arrest.currentLocation || "-",
      housingLocation:
        row.housingLocation ||
        (arrest.housingAssignmentBlock && arrest.housingAssignmentCell && arrest.housingAssignmentBed
          ? `${arrest.housingAssignmentBlock} ${arrest.housingAssignmentCell} ${arrest.housingAssignmentBed} {${arrest.housingAgency}}`
          : "-"),
      rawData: row,
    };
  };

  const onSearchClick = async (e) => {
    e.preventDefault();

    // Cancel previous request if still in flight
    if (cancelSourceRef.current) {
      cancelSourceRef.current.cancel();
    }
    const source = axios.CancelToken.source();
    cancelSourceRef.current = source;

    try {
      setLoading(true);

      let rows = [];

      if (searchByBookingNo) {
        if (!bookingNumberSearch) return;

        const res = await api.get(
          `${API_ENDPOINTS.getBookingByID}${bookingNumberSearch}`,
          { cancelToken: source.token }
        );

        const data = res.data;

        if (data?.bookingNumber) {
          rows = [formatRowForTable(data)];
        }
      } else {
        // 🔹 NAME-BASED SEARCH
        const basePayload = Object.entries(searchParams)
          .filter(([_, v]) => v)
          .map(([k, v]) => ({
            filterKey: FILTER_KEY_MAP[k],
            value: v,
          }));

        const payload = basePayload.some(p => p.filterKey === "searchType")
          ? basePayload
          : [...basePayload, { filterKey: "searchType", value: "Starts With" }];

        if (!payload.length) return;

        const res = await api.post(
          API_ENDPOINTS.bookingsSearch + "?size=500&direction=desc",
          payload,
          { cancelToken: source.token }
        );

        rows = res?.data?.rows || [];
      }

      // 🔹 FORMAT FOR TABLE (handles bio arrays safely)
      const formatted = rows.map(formatRowForTable);

      setTableRows(formatted);
      setSelectedRow(null);
      setTableKey((k) => k + 1);
    } catch (err) {
      if (!axios.isCancel(err)) console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const normalizeBookingDetails = (data) => {
    const arrest = data?.arrestReports?.[0] || {};
    const jacket = data?.jackets?.[0] || {};
    const bio = data?.bio?.[0] || {};
    const charge = data?.charges?.[0] || {};
    const inmate = data?.inmateTracking?.[0] || {};

    return {
      bookingId: data.bookingId || null,
      bookingNumber: data.bookingNumber || null,

      jacketId: jacket.jacketId || null,

      bioId: bio.bioId || null,
      inmateFirstName: bio.fName || null,
      inmateLastName: bio.lName || null,
      inmateMiddleName: bio.mName || null,
      inmateSuffix: bio.suffix || null,
      inmatePin: bio.pinNo || null,

      dateAdded: data.dateAdded || null,
      timeAdded: data.timeAdded || null,

      arrestDate: arrest.arrestDate || null,
      arrestTime: arrest.arrestTime || null,

      scheduledReleaseDate: arrest.scheduledReleaseDate || charge.scheduledReleaseDate || null,
      scheduledReleaseTime: arrest.scheduledReleaseTime || null,

      classificationId: arrest.classificationId || null,

      assignedLocation: (inmate.inJailType + "" + (inmate.block || "")) || null,
      currentLocation: arrest.currentLocation || null,

      dob: bio.dob || null,

      bio,
    };
  };

  const onRowClick = async (row) => {
  const bookingNumber = row.bookingNumber;

  if (selectedRow?.bookingNumber === bookingNumber) {
    setSelectedRow(null);
    setDetailsLoaded(false);
    return;
  }

  if (bookingDetailsCache.current[bookingNumber]) {
    setSelectedRow({
      ...row,
      ...bookingDetailsCache.current[bookingNumber],
    });
    setDetailsLoaded(true);
    return;
  }

  if (bookingDetailsInFlight.current[bookingNumber]) return;
  bookingDetailsInFlight.current[bookingNumber] = true;

  try {
    // setLoading(true);
    setDetailsLoaded(false);

    const res = await api.get(
      `${API_ENDPOINTS.getBookingByID}${bookingNumber}`
    );

    const normalized = normalizeBookingDetails(res.data);
    bookingDetailsCache.current[bookingNumber] = normalized;

    setSelectedRow({
      ...row,
      ...normalized,
    });

    setDetailsLoaded(true);
  } catch (err) {
    console.error("Booking details fetch failed", err);
  } finally {
    // setLoading(false);
    delete bookingDetailsInFlight.current[bookingNumber];
  }
};

  const onSelectCurrentFolder = () => {
    if (!selectedRow) return;

    const newInmate = {
      visitorLogInmateId: null,
      bookingNumber: selectedRow.bookingNumber,
      bioId: selectedRow.bioId,
      inmateFirstName: selectedRow.inmateFirstName,
      inmateLastName: selectedRow.inmateLastName,
      inmateMiddleName: selectedRow.inmateMiddleName,
      inmatePin: selectedRow.inmatePin,
      inmateSuffix: selectedRow.inmateSuffix,
      jacketId: selectedRow.jacketId,

      visitorLogId: bookings.visitorLogId,
      visitorLogInmateExtra: [],
    };

    const inmateExists = bookings.visitorLogInmate?.some(
      (i) => i.bookingNumber === newInmate.bookingNumber
    );

    if (inmateExists) {
      handleClose();
      return;
    }

    const newVisitorLogBooking = {
      visitorLogBookingId: null,
      visitorLogId: bookings.visitorLogId ?? null,
      bookingId: selectedRow.bookingId ?? null,
      visitorLog: [],
    };

    const bookingExists = bookings.visitorLogBooking?.some(
      (b) => b.bookingId === selectedRow.bookingId
    );

    dispatch(
      setBookings({
        ...bookings,

        bookingId: selectedRow.bookingId || null,

        visitorLogInmate: [
          ...(bookings.visitorLogInmate || []),
          newInmate,
        ],

        visitorLogBooking: bookingExists
          ? bookings.visitorLogBooking
          : [
              ...(bookings.visitorLogBooking || []),
              newVisitorLogBooking,
            ],
      })
    );

    handleClose();
  };

  const resetState = () => {
    setSearchByBookingNo(false);
    setBookingNumberSearch("");

    setSearchParams({
      lastName: "",
      middleName: "",
      firstName: "",
      suffix: "",
    });

    formRef.current?.reset();

    setTableRows([]);
    setSelectedRow(null);
    setPagination({ pageIndex: 0, pageSize: 10 });
  };


  const handleClose = () => {
    resetState();
    setShowModal(false);
  };

  const handleImageError = (e) => {
    setBioPhotoUrl(() => defaultAvatar);
  };

  const onSelectedInmateRowClick = (row) => {
    if (selectedInmate?.bookingNumber === row.bookingNumber) {
      setSelectedInmate(null);
    } else {
      setSelectedInmate(row);
    }
  };

  const onDeleteInmate = async () => {
    if (!selectedInmate) return;

    const content = (
      <>
        <p>Are you sure you want to delete this inmate?</p>
        <p>
          Visitor Log Booking No:{" "}
          <strong>{selectedInmate?.bookingNumber ?? "-"}</strong>
        </p>
        <p>
          Inmate Name:{" "}
          <strong>
            {`${selectedInmate?.inmateLastName ?? ""} ${selectedInmate?.inmateFirstName ?? ""}`.trim() || "-"}
          </strong>
        </p>
      </>
    );

    const canDelete = await new Promise((resolve) => {
      showDialog("confirm", {
        title: "Warning",
        content,
        onConfirm: () => resolve(true),
        onCancel: () => resolve(false),
      });
    });

    if (!canDelete) return;

    try {
      setLoading(true);

      const visitorLogInmateId = selectedInmate.visitorLogInmateId;

      // 🔹 CALL DELETE API FIRST
      if (visitorLogInmateId) {
        await api.delete(
          `${API_ENDPOINTS.visitorLog.deleteInmateByID}/${visitorLogInmateId}`
        );
      }

      // 🔹 REMOVE FROM REDUX ONLY AFTER SUCCESS
      const updatedInmates = (bookings.visitorLogInmate || []).filter(
        (i) => i.bookingNumber !== selectedInmate.bookingNumber
      );

      dispatch(
        setBookings({
          ...bookings,
          visitorLogInmate: updatedInmates,
        })
      );

      setSelectedInmate(null);
    } catch (err) {
      console.error("Failed to delete inmate", err);

      showDialog("alert", {
        title: "Error",
        content: "Failed to delete inmate. Please try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  const onDetailsClick = async () => {
    if (!selectedInmate?.bookingNumber) return;

    const bookingNumber = selectedInmate.bookingNumber;
    if (detailsCache.current[bookingNumber]) {
      setDetailsRow(detailsCache.current[bookingNumber]);
      setShowDetailsModal(true);
      return;
    }

    try {
      setLoading(true);
      const res = await api.get(
        `${API_ENDPOINTS.getBookingByID}${bookingNumber}`
      );

      const normalized = normalizeBookingDetails(res.data);
      detailsCache.current[bookingNumber] = normalized;
      setDetailsRow(normalized);
      setShowDetailsModal(true);
    } catch (err) {
      console.error("Failed to load inmate details", err);
    } finally {
      setLoading(false);
    }
  };

  const handleRelationshipChange = (label) => {
    const relationshipId = getRelationshipIdFromLabel(label);

    dispatch(
      setBookings({
        ...bookings,
        visitorLogExtra: [
          {
            visitorLogExtraId:
              bookings?.visitorLogExtra?.[0]?.visitorLogExtraId ?? 0,
            visitorLogId: bookings?.visitorLogId ?? 0,
            visitorRelationshipU: relationshipId, // ✅ STORE ID
          },
        ],
      })
    );
  };

  useEffect(() => {
    const relationship =
      bookings?.visitorLogExtra?.[0]?.visitorRelationshipU;

    if (!relationship) return;

    form.setFieldsValue({
      relationship: relationship, // ✅ STRING
    });
  }, [bookings]);

  const handleCloseDetailsModal = () => {
    setShowDetailsModal(false);
  };

  return (
    <>
      <BookingsSectionTitle style={{ marginBottom: "10px", marginTop: "15px" }}>
        <span>List of Inmates associated with this visit</span>
      </BookingsSectionTitle>
      <StyledGridRow justify="end">
        <StyledGridCol>
          <Button 
            style={{ marginRight: 8 }}
            onClick={() => setShowModal(true)}
            disabled={!bookingsFieldsEnabled}
          >
            ADD
          </Button>
        </StyledGridCol>
        <StyledGridCol>
          <Button 
            style={{ marginRight: 8 }}
            disabled={!bookingsFieldsEnabled || !selectedInmate}
            onClick={onDetailsClick}
          >
            Details
          </Button>
        </StyledGridCol>
        <StyledGridCol>
          <Button
            disabled={!bookingsFieldsEnabled || !selectedInmate}
            onClick={onDeleteInmate}
          >
            Delete
          </Button>
        </StyledGridCol>
      </StyledGridRow>

      <Table
        headers={SELECTED_INMATE_HEADERS}
        data={selectedInmateRows}
        noDataMessage="No inmates added yet"
        style={{ marginTop: "15px" }}
        onRowClick={onSelectedInmateRowClick}
      />

      <BookingsSectionTitle style={{ marginBottom: "10px", marginTop: "15px" }}>
        <span>Additional Visitor Log information</span>
      </BookingsSectionTitle>

      <Form form={form}>
        <StyledGridRow justify="space-between">
          <StyledGridCol lg={6}>
            <FormItem name="relationship" label="Relationship">
              <Select
                options={relationshipOptions}
                placeholder="Relationship"
                showSearch={false}
                variant="outlined"
                onChange={handleRelationshipChange}
                disabled={!bookingsFieldsEnabled}
              />
            </FormItem>
          </StyledGridCol>
        </StyledGridRow>
      </Form>

      <Modal
        header="Add Inmate"
        show={showModal}
        size="xl"
        onHide={handleClose}
        footerActions={[
          {
            key: "select",
            text: "Select Current Folder",
            onClick: onSelectCurrentFolder,
            disabled: !selectedRow, // ✅ single select
          },
        ]}
        footerCloseActionText="Close"
      >
        {/* Search */}
        <form ref={formRef}>
          <Row>
            <Col lg={12}>
              <FormItem>
                <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <input
                    type="checkbox"
                    checked={searchByBookingNo}
                    onChange={(e) => {
                      setSearchByBookingNo(e.target.checked);
                      setBookingNumberSearch("");
                      setSearchParams({
                        lastName: "",
                        middleName: "",
                        firstName: "",
                        suffix: "",
                      });
                    }}
                  />
                  Search by Booking Number
                </label>
              </FormItem>
            </Col>
          </Row>

          {/* 🔹 BOOKING NUMBER SEARCH */}
          {searchByBookingNo ? (
            <Row>
              <Col lg={6}>
                <FormItem label="Booking Number">
                  <TextField
                    value={bookingNumberSearch}
                    onChange={(e) => setBookingNumberSearch(e.target.value)}
                    placeholder="Enter booking number"
                  />
                </FormItem>
              </Col>

              <Col lg={2}>
                <Button style={{ marginTop: 28 }} onClick={onSearchClick}>
                  Search
                </Button>
              </Col>
            </Row>
          ) : (
            /* 🔹 EXISTING NAME SEARCH (UNCHANGED) */
            <Row>
              <Col lg={3}>
                <FormItem label="Last Name">
                  <TextField
                    name="lastName"
                    value={searchParams.lastName}
                    onChange={onChangeSearchParams}
                  />
                </FormItem>
              </Col>

              <Col lg={3}>
                <FormItem label="Middle Name">
                  <TextField
                    name="middleName"
                    value={searchParams.middleName}
                    onChange={onChangeSearchParams}
                  />
                </FormItem>
              </Col>

              <Col lg={3}>
                <FormItem label="First Name">
                  <TextField
                    name="firstName"
                    value={searchParams.firstName}
                    onChange={onChangeSearchParams}
                  />
                </FormItem>
              </Col>

              <Col lg={2}>
                <FormItem label="Suffix">
                  <Select
                    options={suffixOptions}
                    value={searchParams.suffix}
                    onChange={(opt) => onChangeSearchParams(opt, "suffix")}
                  />
                </FormItem>
              </Col>

              <Col lg={1}>
                <Button style={{ marginTop: 28 }} onClick={onSearchClick}>
                  Search
                </Button>
              </Col>
            </Row>
          )}
        </form>

        {/* -------- Selected Inmate Preview -------- */}
        {selectedRow && (
          <div style={{ marginTop: 20, marginBottom: 20, background: "var(--background-color)" }}>
            <Row>
              <Col lg={3}>
                <div className="avatar-container">
                  <Image
                    alt="Default Inmate Avatar"
                    className="avatar"
                    src={bioPhotoUrl}
                    width={200}
                    height={200}
                    onError={handleImageError}
                    fallback={defaultAvatar}
                  />
                </div>
              </Col>

              <Col lg={9}>
                {true && (
                  <InmateDetails fluid>
                    <Row style={{ marginTop: "10px" }}>
                      <InmateInformation>
                        <div className="key">Arrest Date</div>
                        <div className="value justify-content-between">
                          {isNonEmptyVariable(selectedRow?.arrestDate) ? (
                            <span>{selectedRow.arrestDate}</span>
                          ) : (
                            <> - </>
                          )}
                        </div>
                      </InmateInformation>

                      <InmateInformation>
                        <div className="key">Scheduled Release Date</div>
                        <div className="value justify-content-between">
                          {isNonEmptyVariable(selectedRow?.scheduledReleaseDate) ? (
                            <span>{selectedRow.scheduledReleaseDate}</span>
                          ) : (
                            <> - </>
                          )}
                        </div>
                      </InmateInformation>

                      <InmateInformation>
                        <div className="key">Classification</div>
                        <div className="value justify-content-between">
                          {isNonEmptyVariable(selectedRow?.classificationId) ? (
                            <span>{selectedRow.classificationId}</span>
                          ) : (
                            <> - </>
                          )}
                        </div>
                      </InmateInformation>
                    </Row>

                    <Row style={{ marginTop: "10px" }}>
                      <InmateInformation>
                        <div className="key">Assigned Location</div>
                        <div className="value justify-content-between">
                          {isNonEmptyVariable(selectedRow?.assignedLocation) ? (
                            <span>{selectedRow.assignedLocation}</span>
                          ) : (
                            <> - </>
                          )}
                        </div>
                      </InmateInformation>

                      <InmateInformation>
                        <div className="key">Current Location</div>
                        <div className="value justify-content-between">
                          {isNonEmptyVariable(selectedRow?.currentLocation) ? (
                            <span>{selectedRow.currentLocation}</span>
                          ) : (
                            <> - </>
                          )}
                        </div>
                      </InmateInformation>

                    </Row>

                    <Row style={{ marginTop: "10px" }}>
                      <InmateInformation>
                        <div className="key">Pin No</div>
                        <div className="value justify-content-between">
                          {isNonEmptyVariable(selectedRow?.inmatePin) ? (
                            <span>{selectedRow.inmatePin}</span>
                          ) : (
                            <> - </>
                          )}
                        </div>
                      </InmateInformation>

                      <InmateInformation>
                        <div className="key">Booking Number</div>
                        <div className="value justify-content-between">
                          {isNonEmptyVariable(selectedRow?.bookingNumber) ? (
                            <span>{selectedRow.bookingNumber}</span>
                          ) : (
                            <> - </>
                          )}
                        </div>
                      </InmateInformation>

                      <InmateInformation>
                        <div className="key">DOB</div>
                        <div className="value justify-content-between">
                          {isNonEmptyVariable(selectedRow?.dob) ? (
                            <span>{selectedRow.dob}</span>
                          ) : (
                            <> - </>
                          )}
                        </div>
                      </InmateInformation>

                      {/* <InmateInformation>
                        <div className="key">Facility Status</div>
                        <div className="value justify-content-between">
                          {isNonEmptyVariable(selectedRow?.arrestDate) ? (
                            <span>{selectedRow.currentLocation}</span>
                          ) : (
                            <> - </>
                          )}
                        </div>
                      </InmateInformation> */}
                    </Row>
                  </InmateDetails>
                )}
              </Col>
            </Row>
          </div>
        )}

        {/* Table */}
        <div style={{ marginTop: 20 }}>
          <Table
            key={tableKey}
            headers={TABLE_HEADERS}
            data={tableRows}
            totalRecords={tableRows.length}
            isAPiCallLoading={loading}
            pagination={pagination}
            setPagination={setPagination}
            onRowClick={onRowClick}
            noDataMessage="No inmates found"
          />
        </div>
      </Modal>
      <Modal
        header="Inmate Details"
        show={showDetailsModal}
        size="xl"
        onHide={handleCloseDetailsModal}
        footerActions={[
          {
            key: "close",
            text: "Close",
            onClick: handleCloseDetailsModal,
          },
        ]}
      >
        {detailsRow && (
          <div style={{ marginTop: 20, background: "var(--background-color)" }}>
            <Row>
              <Col lg={3}>
                <Image
                  alt="Inmate Avatar"
                  className="avatar"
                  src={detailsRow?.bio?.photoUrl || defaultAvatar}
                  width={200}
                  height={200}
                  fallback={defaultAvatar}
                />
              </Col>

              <Col lg={9}>
                <InmateDetails fluid>
                  <Row style={{ marginTop: "10px" }}>
                    <InmateInformation>
                      <div className="key">Arrest Date</div>
                      <div className="value">
                        {detailsRow.arrestDate || "-"}
                      </div>
                    </InmateInformation>

                    <InmateInformation>
                      <div className="key">Scheduled Release Date</div>
                      <div className="value">
                        {detailsRow.scheduledReleaseDate || "-"}
                      </div>
                    </InmateInformation>

                    <InmateInformation>
                      <div className="key">Classification</div>
                      <div className="value">
                        {detailsRow.classificationId || "-"}
                      </div>
                    </InmateInformation>
                  </Row>

                  <Row style={{ marginTop: "10px" }}>
                    <InmateInformation>
                      <div className="key">Assigned Location</div>
                      <div className="value">
                        {detailsRow.assignedLocation || "-"}
                      </div>
                    </InmateInformation>

                    <InmateInformation>
                      <div className="key">Current Location</div>
                      <div className="value">
                        {detailsRow.currentLocation || "-"}
                      </div>
                    </InmateInformation>
                  </Row>

                  <Row style={{ marginTop: "10px" }}>
                    <InmateInformation>
                      <div className="key">Pin No</div>
                      <div className="value">
                        {detailsRow.inmatePin || "-"}
                      </div>
                    </InmateInformation>

                    <InmateInformation>
                      <div className="key">Booking Number</div>
                      <div className="value">
                        {detailsRow.bookingNumber || "-"}
                      </div>
                    </InmateInformation>

                    <InmateInformation>
                      <div className="key">DOB</div>
                      <div className="value">
                        {detailsRow.dob || "-"}
                      </div>
                    </InmateInformation>
                  </Row>
                </InmateDetails>
              </Col>
            </Row>
          </div>
        )}
      </Modal>
    </>
  );
};

export default InmateToVisit;
